/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import normalizeIndices from '@stdlib/ndarray-base-to-unique-normalized-indices';
import indicesComplement from '@stdlib/array-base-indices-complement';
import takeIndexed from '@stdlib/array-base-take-indexed';


// MAIN //

/**
* Returns the shape defined by the dimensions which are **not** included in a list of dimensions.
*
* @param {NonNegativeIntegerArray} shape - input shape
* @param {IntegerArray} dims - list of dimensions
* @returns {NonNegativeIntegerArray} shape
*
* @example
* var sh = complementShape( [ 3, 2 ], [ 0 ] );
* // returns [ 2 ]
*/
function complementShape( shape, dims ) {
	var ind = normalizeIndices( dims, shape.length-1 );
	if ( ind === null ) {
		// Note: this is an error condition, as `null` is returned when provided out-of-bounds indices...
		return [];
	}
	return takeIndexed( shape, indicesComplement( shape.length, ind ) );
}


// EXPORTS //

export default complementShape;
